import React, { useState, useEffect } from 'react';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import { Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import LockedAccountsTable from '../../components/security/LockedAccountsTable';
import LoginAttemptSettings from '../../components/security/LoginAttemptSettings';
import accountLockoutService from '../../services/accountLockoutService';
import { openAlert } from '../../utils/common';

/**
 * 계정 보안 관리 페이지
 */
const SecurityManagementPage = () => {
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const { role, isLoading: roleLoading } = useAuth();

  // AuthContext의 역할 정보를 기반으로 권한 확인 및 데이터 로드
  useEffect(() => {
    if (roleLoading) {
      return; // 역할 정보 로딩 중이면 대기
    }
    
    // super_admin 또는 admin 권한 모두 허용 (테스트를 위해)
    const isSuperAdmin = role === 'super_admin' || role === 'admin';
    
    setAuthorized(isSuperAdmin);
    
    if (isSuperAdmin) {
      fetchLockedAccounts();
    }
  }, [role, roleLoading]);

  // 잠긴 계정 목록 가져오기
  const fetchLockedAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await accountLockoutService.getLockedAccounts();
      setLockedAccounts(data || []);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || '잠긴 계정 목록을 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
      openAlert({ title: '알림', description: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // 계정 잠금 해제 후 목록 새로고침
  const handleAccountUnlocked = (unlockedUserIdx) => {
    // 목록에서 해당 계정 제거
    setLockedAccounts(lockedAccounts.filter(account => account.idx !== unlockedUserIdx));
  };

  // 새로고침 버튼 핸들러
  const handleRefresh = () => {
    fetchLockedAccounts();
  };

  // 권한이 없을 경우
  if (!authorized) {
    return (
      <SectionContainer title="계정 보안 관리">
        <Alert variant="danger">
          <Alert.Heading>접근 권한이 없습니다</Alert.Heading>
          <p>이 페이지는 슈퍼관리자만 접근할 수 있습니다.</p>
        </Alert>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer title="계정 보안 관리">
      <Spinner visible={loading} />
      <Row className="mb-4">
        <Col>
          <LoginAttemptSettings />
        </Col>
      </Row>
      
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">잠긴 계정 관리</h5>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
              >
                새로고침
              </Button>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              <LockedAccountsTable 
                accounts={lockedAccounts} 
                onUnlock={handleAccountUnlocked} 
                loading={loading}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </SectionContainer>
  );
};

export default SecurityManagementPage;
